import { useEffect, useState, type RefObject } from 'react';

const DEFAULT_EXPANDED_HEIGHT = 320;
const MIN_EXPANDED_HEIGHT = 160;
const HEADER_HEIGHT = 64;
const STICKY_BOTTOM_GAP = 16;
const MAX_HEIGHT_SAFETY_BUFFER = 50;

export { MIN_EXPANDED_HEIGHT };

export function useComposerExpansion(
  expandable: boolean,
  formRef: RefObject<HTMLFormElement | null>,
  editorRef: RefObject<HTMLDivElement | null>,
) {
  const [isFullyExpanded, setIsFullyExpanded] = useState(false);
  const [expandedHeight, setExpandedHeight] = useState(DEFAULT_EXPANDED_HEIGHT);
  const [maxExpandedHeight, setMaxExpandedHeight] = useState(
    DEFAULT_EXPANDED_HEIGHT,
  );

  const recomputeMaxExpandedHeight = () => {
    if (!formRef.current || !editorRef.current) return;
    const formHeight = formRef.current.getBoundingClientRect().height;
    const editorHeight = editorRef.current.getBoundingClientRect().height;
    const chromeHeight = formHeight - editorHeight;
    const availableParentHeight =
      window.innerHeight - HEADER_HEIGHT - STICKY_BOTTOM_GAP;
    const nextMax = Math.max(
      MIN_EXPANDED_HEIGHT,
      Math.floor(
        availableParentHeight - chromeHeight - MAX_HEIGHT_SAFETY_BUFFER,
      ),
    );
    setMaxExpandedHeight(nextMax);
    setExpandedHeight((current) => Math.min(current, nextMax));
  };

  useEffect(() => {
    if (!expandable || !isFullyExpanded) return;
    window.addEventListener('resize', recomputeMaxExpandedHeight);
    return () =>
      window.removeEventListener('resize', recomputeMaxExpandedHeight);
  }, [expandable, isFullyExpanded]);

  const handleExpandCollapse = () => {
    if (!isFullyExpanded) recomputeMaxExpandedHeight();
    setIsFullyExpanded((prev) => !prev);
  };

  return {
    isFullyExpanded,
    setIsFullyExpanded,
    expandedHeight,
    setExpandedHeight,
    maxExpandedHeight,
    handleExpandCollapse,
  };
}
